/**
 * YOJO-3428 [an open care-portal tab breaks on the next click after every deploy; index.html cached an hour]
 *
 * Every build names its chunks by content hash, and a deploy removes the old ones. A tab opened
 * before the deploy still asks for the old names on its next lazy route; Firebase's catch-all
 * rewrite answers those with index.html, so the import fails ("Failed to fetch dynamically
 * imported module"). Vite then dispatches a cancelable `vite:preloadError` event on `window` and
 * rethrows unless the event is cancelled.
 *
 * On that event we reload once, which fetches the new index.html and its new chunks. The time of
 * the reload is kept in sessionStorage: a second failure within PRELOAD_RELOAD_WINDOW_MS (30 s) is
 * not a stale tab but a real failure, so it is left to reach the error boundary (never a reload
 * loop). A later deploy in the same tab is hours away, outside the window, and reloads again.
 *
 * Once a reload is under way the page is "reloading" (one flag per page load, read through
 * isReloadingForStaleChunk): every further `vite:preloadError` on the page (Vite raises one per
 * failed CSS preload plus one for the module import) is cancelled without a second reload. A
 * cancelled event makes React.lazy throw during render before the reload lands, so the error
 * boundaries show the loading spinner and Sentry drops the event while the flag is set.
 *
 * Storage can throw (private windows, blocked storage, full quota). Without a stamp there is no
 * loop guard, so there is no reload either: the event is not cancelled and the error reaches the
 * error boundary as before. The listener never throws.
 */
export const PRELOAD_RELOAD_KEY = 'yojo_preload_error_reload_at';

export const PRELOAD_RELOAD_WINDOW_MS = 30_000;

const PRELOAD_ERROR_EVENT = 'vite:preloadError';

// One flag per page load: set right before the reload, reset by each install.
let reloading = false;

/** True once this page has started a reload for a stale chunk. */
export function isReloadingForStaleChunk(): boolean {
  return reloading;
}

function readLastReload(win: Window): number | null | undefined {
  try {
    const stored = win.sessionStorage.getItem(PRELOAD_RELOAD_KEY);
    const at = stored === null ? NaN : Number(stored);
    return Number.isFinite(at) ? at : null;
  } catch {
    // No storage: the caller does not reload
    return undefined;
  }
}

function stampReload(win: Window, at: number): boolean {
  try {
    win.sessionStorage.setItem(PRELOAD_RELOAD_KEY, String(at));
    return true;
  } catch {
    return false;
  }
}

/** Installs the listener; returns a function that removes it again. */
export function installPreloadErrorRecovery(win: Window = window, now: () => number = Date.now): () => void {
  reloading = false;

  const onPreloadError = (event: Event) => {
    try {
      if (reloading) {
        event.preventDefault();
        return;
      }

      const at = now();
      const lastReload = readLastReload(win);
      if (lastReload === undefined) {
        return;
      }
      if (lastReload !== null && at - lastReload < PRELOAD_RELOAD_WINDOW_MS) {
        return;
      }
      if (!stampReload(win, at)) {
        return;
      }

      event.preventDefault();
      reloading = true;
      win.location.reload();
    } catch {
      // Never throw from the listener: the error then reaches the error boundary as before
    }
  };

  win.addEventListener(PRELOAD_ERROR_EVENT, onPreloadError);
  return () => win.removeEventListener(PRELOAD_ERROR_EVENT, onPreloadError);
}
